'use client';

import { useRef } from 'react';

import Arrow from '@/components/ui/Arrow';
import CarouselDots from '@/components/ui/CarouselDots';
import ServiceCard, { Service } from '@/features/services/ServiceCard';
import { useCarousel } from '@/hooks/useCarousel';
import { useCarouselMediaHeight } from '@/hooks/useCarouselMediaHeight';
import { COMMON_I18N } from '@/lib/i18n/commonI18n';
import { resolveAppLocale } from '@/lib/i18n/routing';

const ServiceList = ({ servicesList, locale, label }: ServiceListProps) => {
  const t = COMMON_I18N[resolveAppLocale(locale)];
  const viewportWrapRef = useRef<HTMLDivElement | null>(null);

  const {
    emblaRef,
    selectedIndex,
    canPrev,
    canNext,
    snapCount,
    scrollPrev,
    scrollNext,
    scrollTo,
  } = useCarousel();

  const mediaHeight = useCarouselMediaHeight(viewportWrapRef);

  return (
    <div className="relative">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10"
        style={{ height: mediaHeight || undefined }}
      >
        <Arrow
          direction="prev"
          label={t.carouselPrevious}
          disabled={!canPrev}
          onClick={scrollPrev}
          className="hidden tablet:flex left-2 pointer-events-auto"
        />
        <Arrow
          direction="next"
          label={t.carouselNext}
          disabled={!canNext}
          onClick={scrollNext}
          className="hidden tablet:flex right-2 pointer-events-auto"
        />
      </div>

      <div
        aria-roledescription="carousel"
        aria-label={label}
        className="overflow-hidden"
        ref={(node) => {
          viewportWrapRef.current = node;
          emblaRef(node);
        }}
      >
        <div className="-mx-[10px] flex items-stretch">
          {servicesList.map((service, i) => (
            <div
              key={service.slug}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} ${t.slideOf} ${servicesList.length}`}
              className="box-border shrink-0 basis-full px-[10px] min-w-0 tablet:basis-1/2 desktop:basis-1/3"
            >
              <div className="w-full min-w-0 h-full">
                <ServiceCard service={service} locale={locale} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <CarouselDots
          count={snapCount}
          selectedIndex={selectedIndex}
          onSelect={scrollTo}
          label={label}
          slideOf={t.slideOf}
        />
      </div>
    </div>
  );
};

export default ServiceList;

type ServiceListProps = {
  servicesList: Array<Service>;
  locale: string;
  label: string;
};
